import React from "react";
import { Link } from "react-router-dom";
import { Check, Users, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { ChecklistItem } from "@/model/interface/Onboarding";
import { CHECKLIST_ICON_MAP } from "./icons";

interface ChecklistRowProps {
  item: ChecklistItem;
  onSkip: () => void;
}

export default function ChecklistRow({ item, onSkip }: ChecklistRowProps) {
  const Icon = CHECKLIST_ICON_MAP[item.icon] ?? Users;
  const isDone = item.status === "COMPLETED" || item.status === "SKIPPED";

  return (
    <div className="flex items-start gap-3 py-3">
      <div
        className={cn(
          "flex h-8 w-8 shrink-0 items-center justify-center rounded-full",
          isDone ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground",
        )}
      >
        {isDone ? <Check className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
      </div>

      <div className="flex-1 min-w-0">
        <div className={cn("font-medium", isDone && "line-through text-muted-foreground")}>
          {item.title}
        </div>
        <div className="text-sm text-muted-foreground">{item.description}</div>
      </div>

      {!isDone && (
        <div className="flex items-center gap-1 shrink-0">
          {item.ctaRoute && item.ctaLabel && (
            <Button size="sm" asChild>
              <Link to={item.ctaRoute}>{item.ctaLabel}</Link>
            </Button>
          )}
          {item.skippable && (
            <Button size="icon" variant="ghost" onClick={onSkip} title="Skip">
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      )}

      {item.status === "SKIPPED" && (
        <span className="text-xs text-muted-foreground self-center">Skipped</span>
      )}
    </div>
  );
}
